import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, eur, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Search as SearchIcon, X, Pencil, Trash2, ArrowLeft, Sparkles,
  TrendingUp, MousePointerClick, Eye as EyeIcon,
} from "lucide-react";

/**
 * Fase 16 — Suggerimenti di vendita (upsell / cross-sell).
 * Admin-driven rules, no AI.
 */

const KINDS = [
  { v: "recommended",  label: "Consigliato",  emoji: "✨" },
  { v: "alternative",  label: "Alternativo",  emoji: "↔" },
  { v: "premium",      label: "Upgrade Premium", emoji: "⭐" },
  { v: "addon",        label: "Addon",       emoji: "＋" },
  { v: "wine_pairing", label: "Abbinamento Vino", emoji: "🍷" },
];

const kindLabel = (k) => KINDS.find((x) => x.v === k)?.label || k;

function normalise(s) { return (s || "").toString().toLowerCase(); }

function SuggestionDialog({ suggestion, products, onSaved, trigger }) {
  const isEdit = !!suggestion;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    source_product_id: "", target_product_id: "",
    kind: "recommended", priority: 10,
    conditions: { require_available: true,
                  require_category_available: true,
                  require_department_available: true },
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(isEdit ? {
      source_product_id: suggestion.source_product_id,
      target_product_id: suggestion.target_product_id,
      kind: suggestion.kind, priority: suggestion.priority,
      conditions: { ...(suggestion.conditions || {}) },
    } : {
      source_product_id: "", target_product_id: "",
      kind: "recommended", priority: 10,
      conditions: { require_available: true,
                    require_category_available: true,
                    require_department_available: true },
    });
  }, [open, isEdit, suggestion]);

  const save = async () => {
    if (!f.source_product_id || !f.target_product_id)
      return toast.error("Seleziona sorgente e target");
    if (f.source_product_id === f.target_product_id)
      return toast.error("Sorgente e target devono essere diversi");
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/suggestions/${suggestion.id}`, f);
      else        await api.post(`/admin/suggestions`, f);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Regola aggiornata" : "Regola creata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const productOptions = useMemo(() => (
    [...products].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
  ), [products]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="rounded-full" data-testid="sugg-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova regola
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica regola" : "Nuova regola di vendita"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Se il cliente apre / ordina</Label>
            <Select value={f.source_product_id}
                    onValueChange={(v) => setF({ ...f, source_product_id: v })}>
              <SelectTrigger data-testid="sugg-source"><SelectValue placeholder="Prodotto sorgente" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {productOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Suggerisci</Label>
            <Select value={f.target_product_id}
                    onValueChange={(v) => setF({ ...f, target_product_id: v })}>
              <SelectTrigger data-testid="sugg-target"><SelectValue placeholder="Prodotto target" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {productOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger data-testid="sugg-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.v} value={k.v}>{k.emoji} {k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorità (1 = prima)</Label>
              <Input type="number" min="1" value={f.priority}
                     onChange={(e) => setF({ ...f, priority: parseInt(e.target.value) || 1 })}
                     data-testid="sugg-priority" />
            </div>
          </div>
          <div className="rounded-xl border border-sand-300 p-3 bg-sand-50/40 space-y-2">
            <Label className="text-xs">Mostra solo se…</Label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.conditions.require_available !== false}
                      onCheckedChange={(v) => setF({ ...f,
                        conditions: { ...f.conditions, require_available: v } })}
                      data-testid="sugg-cond-avail" />
              Prodotto disponibile
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.conditions.require_category_available !== false}
                      onCheckedChange={(v) => setF({ ...f,
                        conditions: { ...f.conditions, require_category_available: v } })}
                      data-testid="sugg-cond-cat" />
              Categoria visibile
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.conditions.require_department_available !== false}
                      onCheckedChange={(v) => setF({ ...f,
                        conditions: { ...f.conditions, require_department_available: v } })}
                      data-testid="sugg-cond-dept" />
              Reparto attivo
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="sugg-save">
            {busy ? "Salvo…" : (isEdit ? "Salva" : "Crea")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnalyticsCard({ analytics }) {
  if (!analytics) return null;
  const t = analytics.total || {};
  return (
    <section className="rounded-2xl border border-sand-300 p-4 bg-white space-y-3"
             data-testid="sugg-analytics">
      <header className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4" />
        <h2 className="font-semibold">Analytics · ultimi {analytics.days} giorni</h2>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl bg-sand-50 p-3">
          <div className="text-xs text-ink-500 flex items-center gap-1"><EyeIcon className="w-3 h-3" /> Visualizzazioni</div>
          <div className="text-2xl font-serif" data-testid="analytics-views">{t.views || 0}</div>
        </div>
        <div className="rounded-xl bg-sand-50 p-3">
          <div className="text-xs text-ink-500 flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> Click</div>
          <div className="text-2xl font-serif" data-testid="analytics-clicks">{t.clicks || 0}</div>
        </div>
        <div className="rounded-xl bg-sand-50 p-3">
          <div className="text-xs text-ink-500">Conversioni</div>
          <div className="text-2xl font-serif" data-testid="analytics-conversions">{t.conversions || 0}</div>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3">
          <div className="text-xs text-emerald-700">Ricavo generato</div>
          <div className="text-2xl font-serif text-emerald-800" data-testid="analytics-value">
            {eur(t.value_cents || 0)}
          </div>
        </div>
      </div>
      {(analytics.top?.length > 0 || analytics.worst?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold text-emerald-800 mb-1">🏆 Top suggerimenti</div>
            <ul className="space-y-1 text-sm">
              {(analytics.top || []).slice(0, 5).map((it) => (
                <li key={it.id} className="rounded-lg bg-emerald-50/50 border border-emerald-200 px-2 py-1"
                    data-testid={`sugg-top-${it.id}`}>
                  <span className="font-medium">{it.source_product_name}</span>
                  {" → "}<span>{it.target_product_name}</span>
                  <span className="text-xs text-ink-500 ml-2">
                    {it.conversions} conv · {eur(it.value_cents || 0)}
                  </span>
                </li>
              ))}
              {(!analytics.top || analytics.top.length === 0) && (
                <li className="text-xs text-ink-500 italic">Nessuna conversione ancora.</li>
              )}
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold text-amber-800 mb-1">⚠ Peggiori (mai cliccati)</div>
            <ul className="space-y-1 text-sm">
              {(analytics.worst || []).slice(0, 5).map((it) => (
                <li key={it.id} className="rounded-lg bg-amber-50/50 border border-amber-200 px-2 py-1"
                    data-testid={`sugg-worst-${it.id}`}>
                  <span className="font-medium">{it.source_product_name}</span>
                  {" → "}<span>{it.target_product_name}</span>
                  <span className="text-xs text-ink-500 ml-2">{it.views} view · 0 click</span>
                </li>
              ))}
              {(!analytics.worst || analytics.worst.length === 0) && (
                <li className="text-xs text-ink-500 italic">Tutto ok!</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminSuggestions() {
  const [items, setItems]         = useState([]);
  const [products, setProducts]   = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [kindFilter, setKindFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/admin/suggestions"),
        api.get("/admin/products"),
        api.get("/admin/suggestions/analytics?days=30"),
      ]);
      setItems(r1.data || []);
      setProducts(r2.data || []);
      setAnalytics(r3.data || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const analyticsById = useMemo(() => {
    const m = {};
    (analytics?.items || []).forEach((it) => { m[it.id] = it; });
    return m;
  }, [analytics]);

  const filtered = useMemo(() => {
    const nq = normalise(q).trim();
    return items.filter((it) => {
      if (kindFilter && it.kind !== kindFilter) return false;
      if (!nq) return true;
      return normalise(it.source_product_name).includes(nq)
          || normalise(it.target_product_name).includes(nq);
    });
  }, [items, q, kindFilter]);

  const doDelete = async (it) => {
    if (!window.confirm(`Eliminare la regola?\n${it.source_product_name} → ${it.target_product_name}`)) return;
    try {
      await api.delete(`/admin/suggestions/${it.id}`);
      toast.success("Regola eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Group by source product for a compact view.
  const grouped = useMemo(() => {
    const byId = {};
    filtered.forEach((it) => {
      const k = it.source_product_id;
      (byId[k] = byId[k] || {
        source_id: k, source_name: it.source_product_name,
        rows: []
      }).rows.push(it);
    });
    return Object.values(byId).sort((a, b) => (a.source_name || "").localeCompare(b.source_name || ""));
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="sugg-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/menu" className="btn-outline text-xs py-1 px-3" data-testid="sugg-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Menu
        </Link>
        <div>
          <h1 className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Suggerimenti di vendita
          </h1>
          <p className="text-xs text-ink-500">
            Cross-selling e upsell configurati manualmente. Zero AI.
          </p>
        </div>
        <div className="ml-auto">
          <SuggestionDialog products={products} onSaved={load} />
        </div>
      </div>

      <AnalyticsCard analytics={analytics} />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca prodotto…" className="pl-9 pr-8"
                 data-testid="sugg-search" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400"
                    aria-label="Pulisci" data-testid="sugg-search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={kindFilter || "all"}
                onValueChange={(v) => setKindFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="max-w-[220px]" data-testid="sugg-kind-filter">
            <SelectValue placeholder="Tutti i tipi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {KINDS.map((k) => (
              <SelectItem key={k.v} value={k.v}>{k.emoji} {k.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-6 text-ink-500" data-testid="sugg-loading">Caricamento…</div>
      ) : grouped.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-sm text-ink-500"
             data-testid="sugg-empty">
          {items.length === 0
            ? "Nessuna regola. Crea la prima e vedi salire lo scontrino medio."
            : "Nessun risultato per i filtri correnti."}
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div key={g.source_id} className="rounded-2xl border border-sand-300 bg-white p-3"
                 data-testid={`sugg-src-${g.source_id}`}>
              <div className="text-sm text-ink-500 mb-2">
                Quando il cliente apre <b className="text-ink-900">{g.source_name}</b>:
              </div>
              <ul className="space-y-1.5">
                {g.rows.sort((a, b) => a.priority - b.priority).map((it) => {
                  const s = analyticsById[it.id];
                  return (
                    <li key={it.id} data-testid={`sugg-row-${it.id}`}
                        className="rounded-xl border border-sand-200 px-3 py-2 flex items-center gap-2 flex-wrap">
                      <span className="text-xs rounded-full bg-sand-100 px-2 py-0.5">
                        #{it.priority}
                      </span>
                      <span className="text-xs rounded-full bg-bronze-50 border border-bronze-200 text-bronze-800 px-2 py-0.5">
                        {(KINDS.find((k) => k.v === it.kind) || {}).emoji}{" "}
                        {kindLabel(it.kind)}
                      </span>
                      <span className="font-medium">{it.target_product_name}</span>
                      {s && (
                        <span className="text-xs text-ink-500 ml-2">
                          {s.views || 0} 👁 · {s.clicks || 0} 🖱 · {s.conversions || 0} ✅
                          {s.value_cents > 0 && <> · <b>{eur(s.value_cents)}</b></>}
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <SuggestionDialog suggestion={it} products={products} onSaved={load}
                          trigger={
                            <button className="p-1 rounded-full hover:bg-sand-200"
                                    data-testid={`sugg-edit-${it.id}`} title="Modifica">
                              <Pencil className="w-4 h-4" />
                            </button>
                          } />
                        <button onClick={() => doDelete(it)}
                                className="p-1 rounded-full text-red-700 hover:bg-red-50"
                                data-testid={`sugg-del-${it.id}`} title="Elimina">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
